import {
  ameexTracking,
  ameexParcelInfo,
  ozonTracking,
  ozonParcelInfo,
  onestaTracking,
  onestaParcelInfo,
  forcelogParcelInfo,
  forcelogTracking,
  olivraisonTracking,
  trackShipment,
  getAgencyConnect
} from "./auth";
import {sendit_traking } from "./Api_sendit_front";


function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function cleanText(value) {
  return String(value || "").trim();
}

function normalizeOzonTrackingResponse(
  trackingNumber,
  trackingRes,
  parcelRes = null
) {
  const response = trackingRes || {};
  const ozonBody = response?.ozon_body || response || {};
  const tracking = ozonBody?.TRACKING || {};
  const historyRaw = tracking?.HISTORY || {};
  const lastTracking = tracking?.LAST_TRACKING || {};

  const history = Object.values(historyRaw).map((item) => ({
    status: cleanText(item?.STATUT) || "Tracking update",
    date: cleanText(item?.TIME_STR) || cleanText(item?.TIME) || null,
    location: cleanText(item?.COMMENT) || "—",
    raw: item,
  }));

  return {
    provider: "ozon",
    trackingNumber:
      cleanText(response?.tracking_number) ||
      cleanText(tracking?.["TRACKING-NUMBER"]) ||
      trackingNumber,

    trackingStatus:
      cleanText(response?.ozon_status) ||
      cleanText(lastTracking?.STATUT) ||
      cleanText(history?.[history.length - 1]?.status) ||
      "Tracking available",

    lastUpdate:
      cleanText(lastTracking?.TIME_STR) ||
      cleanText(lastTracking?.TIME) ||
      cleanText(history?.[history.length - 1]?.date) ||
      null,

    history,
    parcel: parcelRes || response || null,
    raw: {
      tracking: response,
      parcel: parcelRes,
    },
  };
}

function extractParcelCodeFromUrl(url) {
  const value = String(url || "");
  const match = value.match(/[?&]ParcelCode=([^&]+)/i);
  return match?.[1] || null;
}

function normalizeAmeexTrackingResponse(
  trackingNumber,
  trackingRes,
  parcelRes = null
) {
  const response = trackingRes || {};
  const ameexResponse = response?.ameex_response || {};
  const apiData = ameexResponse?.api || {};
  const trackingList = Array.isArray(apiData?.tracking) ? apiData.tracking : [];

  const history = trackingList.map((item) => ({
    status: item?.statut_name || item?.statut || "Tracking update",
    date: item?.time_str || item?.time || null,
    location: item?.city_name || item?.hub_name || item?.comment || "—",
    raw: item,
  }));

  const responseTrackingNumber =
    extractParcelCodeFromUrl(response?.request_url) || trackingNumber;

  return {
    provider: "ameex",
    trackingNumber: responseTrackingNumber,
    trackingStatus:
      response?.last_tracking_status_name ||
      response?.last_tracking_status_code ||
      history?.[history.length - 1]?.status ||
      "Tracking available",
    lastUpdate:
      response?.last_tracking_occurred_at ||
      history?.[history.length - 1]?.date ||
      null,
    history,
    parcel: parcelRes || response || null,
    raw: {
      tracking: response,
      parcel: parcelRes,
    },
    syncedOrder: response?.orders_update?.body || null,
  };
}

function normalizeOnestaTrackingResponse(
  trackingNumber,
  trackingRes,
  parcelRes = null
) {
  const response = trackingRes || parcelRes || {};
  const onestaResponse = response?.onesta_response || {};
  const data = onestaResponse?.data || {};
  const historyRaw = Array.isArray(data?.history) ? data.history : [];

  const history = historyRaw.map((item) => ({
    status: item?.name || item?.status || "Tracking update",
    date: item?.created_at || item?.new_date || null,
    location:
      item?.delivery_infos?.city ||
      item?.delivery_infos?.agency ||
      item?.delivery_infos?.comment ||
      "—",
    raw: item,
  }));

  const latestRaw = historyRaw[0] || historyRaw[historyRaw.length - 1] || null;
  const latestHistory = history[0] || history[history.length - 1] || null;

  return {
    provider: "onesta",
    trackingNumber,
    trackingStatus:
      latestRaw?.name ||
      latestHistory?.status ||
      response?.latest_status ||
      onestaResponse?.message ||
      "Tracking available",
    lastUpdate:
      latestRaw?.created_at ||
      latestHistory?.date ||
      null,
    history,
    parcel: parcelRes || trackingRes || null,
    raw: {
      tracking: trackingRes,
      parcel: parcelRes,
    },
  };
}

function normalizeForcelogTrackingResponse(trackingNumber, parcelRes = null) {
  const response = parcelRes || {};
  const apiResponse = response?.forcelog_response || {};
  const getParcel = apiResponse?.["GET-PARCEL"] || {};
  const parcel = getParcel?.PARCEL || {};
  const agent = parcel?.DELIVERY_AGENT || {};

  const resolvedTrackingNumber =
    cleanText(parcel?.TRACKING_NUMBER) || cleanText(trackingNumber);

  const resolvedStatus =
    cleanText(response?.extracted_status) ||
    cleanText(parcel?.STATUS) ||
    cleanText(parcel?.STATUS_CODE) ||
    cleanText(getParcel?.MESSAGE) ||
    "Tracking available";

  const resolvedLastUpdate = cleanText(parcel?.CREATION_TIME) || null;

  const history = resolvedStatus
    ? [
        {
          status: resolvedStatus,
          date: resolvedLastUpdate,
          location: cleanText(parcel?.CITY_NAME) || "—",
          note:
            cleanText(parcel?.SECONDARY_STATUS) ||
            cleanText(parcel?.SITUATION) ||
            cleanText(getParcel?.MESSAGE) ||
            "Parcel found",
          raw: parcel,
        },
      ]
    : [];

  return {
    provider: "forcelog",
    trackingNumber: resolvedTrackingNumber,
    trackingStatus: resolvedStatus,
    lastUpdate: resolvedLastUpdate,
    history,
    parcel: response,
    raw: {
      tracking: null,
      parcel: response,
    },
    meta: {
      receiver: cleanText(parcel?.RECEIVER) || null,
      phone: cleanText(parcel?.PHONE) || null,
      city: cleanText(parcel?.CITY_NAME) || null,
      address: cleanText(parcel?.ADDRESS) || null,
      canOpen: cleanText(parcel?.CAN_OPEN) || null,
      situation: cleanText(parcel?.SITUATION) || null,
      statusCode: cleanText(parcel?.STATUS_CODE) || null,
      deliveryAgentName: cleanText(agent?.NAME) || null,
      deliveryAgentPhone: cleanText(agent?.PHONE) || null,
    },
  };
}

/* =========================================================
   PROVIDERS
========================================================= */

function normalizeSenditTrackingResponse(trackingNumber, trackingRes) {
  const data = trackingRes?.data || {};

  const audits = Array.isArray(data?.audits) ? data.audits : [];

  const history = audits.map((item) => ({
    status: item?.data?.status || item?.event || "Tracking update",
    date: item?.created_at || null,
    location: item?.user || "Sendit",
    raw: item,
  }));

  return {
    provider: "sendit",
    trackingNumber: data?.code || trackingNumber,

    trackingStatus:
      data?.status ||
      history?.[history.length - 1]?.status ||
      "Tracking available",

    lastUpdate:
      data?.last_action_at ||
      history?.[history.length - 1]?.date ||
      null,

    history,

    parcel: data,
    raw: {
      tracking: trackingRes,
      parcel: data,
    },
  };
}

const trackingProviders = {
  sendit: {
  match: ({ carrier, agencyId }) => {
    const value = normalizeText(carrier);
    return (
      value.includes("sendit") ||
      String(agencyId || "") === "b19e0f1f-368e-11f1-867f-0200c32b60b0"
    );
  },

  async fetch({ trackingNumber }) {
        const agencyLivraison = await getAgencyConnect();
        const defaultagencyLivraison = agencyLivraison.find((a) => a.is_default);
        const trackingRes = await sendit_traking(trackingNumber , defaultagencyLivraison);
    return normalizeSenditTrackingResponse(
      trackingNumber,
      trackingRes
    );
  },
},

  ameex: {
    match: ({ carrier, agencyId }) => {
      const value = normalizeText(carrier);
      return (
        value.includes("ameex") ||
        String(agencyId || "").toUpperCase() === "AMEEX"
      );
    },

    async fetch({ trackingNumber, agencyId }) {
      const [trackingRes, parcelRes] = await Promise.allSettled([
        ameexTracking(trackingNumber, agencyId),
        ameexParcelInfo(trackingNumber, agencyId),
      ]);

      const trackingValue =
        trackingRes.status === "fulfilled" ? trackingRes.value : null;

      const parcelValue =
        parcelRes.status === "fulfilled" ? parcelRes.value : null;

      return normalizeAmeexTrackingResponse(
        trackingNumber,
        trackingValue,
        parcelValue
      );
    },
  },

  ozon: {
    match: ({ carrier }) => normalizeText(carrier).includes("ozon"),

    async fetch({ trackingNumber, agencyId }) {
      const [trackingRes, parcelRes] = await Promise.allSettled([
        ozonTracking(trackingNumber),
        ozonParcelInfo(trackingNumber, agencyId),
      ]);

      const trackingValue =
        trackingRes.status === "fulfilled" ? trackingRes.value : null;

      const parcelValue =
        parcelRes.status === "fulfilled" ? parcelRes.value : null;

      return normalizeOzonTrackingResponse(
        trackingNumber,
        trackingValue,
        parcelValue
      );
    },
  },

  onesta: {
    match: ({ carrier }) => {
      const value = normalizeText(carrier);
      return value.includes("onesta") || value.includes("onessta");
    },

    async fetch({ trackingNumber, agencyId }) {
      const [trackingRes, parcelRes] = await Promise.allSettled([
        onestaTracking(trackingNumber, agencyId),
        onestaParcelInfo(trackingNumber, agencyId),
      ]);

      const trackingValue =
        trackingRes.status === "fulfilled" ? trackingRes.value : null;

      const parcelValue =
        parcelRes.status === "fulfilled" ? parcelRes.value : null;

      return normalizeOnestaTrackingResponse(
        trackingNumber,
        trackingValue,
        parcelValue
      );
    },
  },

  forcelog: {
    match: ({ carrier }) => {
      const value = normalizeText(carrier);
      return value.includes("forcelog") || value.includes("forclog");
    },

    async fetch({ trackingNumber }) {
      const [parcelRes, trackingRes] = await Promise.allSettled([
        forcelogParcelInfo(trackingNumber),
        forcelogTracking(trackingNumber),
      ]);
      const normalized = normalizeForcelogTrackingResponse(
        trackingNumber,
        parcelRes.status === "fulfilled" ? parcelRes.value : null
      );
      // Timeline complète (GetTracking) quand elle est disponible
      const history = trackingRes.status === "fulfilled" ? trackingRes.value?.history || [] : [];
      if (history.length) {
        normalized.history = history.map((item) => ({
          status: cleanText(item?.STATUS_NAME) || cleanText(item?.STATUS_CODE) || "Tracking update",
          date: cleanText(item?.TIME) || null,
          location: cleanText(item?.CITY_NAME) || "—",
          raw: item,
        }));
        const last = history[history.length - 1];
        normalized.trackingStatus = cleanText(last?.STATUS_NAME) || normalized.trackingStatus;
        normalized.lastUpdate = cleanText(last?.TIME) || normalized.lastUpdate;
        normalized.raw.tracking = trackingRes.value;
      }
      return normalized;
    },
  },

  olivraison: {
    match: ({ carrier, agencyId }) =>
      normalizeText(carrier).includes("olivraison") ||
      String(agencyId || "").toUpperCase() === "OLIVRAISON",

    async fetch({ trackingNumber }) {
      const res = await olivraisonTracking(trackingNumber);
      const history = (res?.history || []).map((item) => ({
        status: cleanText(item?.status) || "Tracking update",
        date: cleanText(item?.updateAt) || null,
        location: cleanText(item?.msg) || "—",
        note: cleanText(item?.reportedTo) ? `Reporté au ${cleanText(item.reportedTo)}` : undefined,
        raw: item,
      }));
      return {
        provider: "olivraison",
        trackingNumber: res?.trackingID || trackingNumber,
        trackingStatus: cleanText(res?.status) || history[history.length - 1]?.status || "Tracking available",
        lastUpdate: history[history.length - 1]?.date || null,
        history,
        parcel: res?.olivraison_response || null,
        raw: { tracking: res, parcel: res?.olivraison_response || null },
        meta: {
          deliveryAgentName: cleanText(res?.transport?.currentDriverName) || null,
          deliveryAgentPhone: cleanText(res?.transport?.currentDriverPhone) || null,
        },
      };
    },
  },
};

export async function getOrderTrackingData(order) {
  const trackingNumber =
    order?.trackingNumber ||
    order?.delivery_tracking_id ||
    order?.raw_payload?.shipping?.tracking_number;

  const agencyId = order?.delivery_agency_id;
  const carrier = order?.carrier || "";

  if (!trackingNumber || !agencyId) {
    return {
      provider: null,
      trackingNumber: trackingNumber || null,
      trackingStatus: "No tracking available",
      lastUpdate: null,
      history: [],
      parcel: null,
      raw: null,
    };
  }

  // 1) API unifiée : agence ET compte qui ont réellement expédié la commande
  //    (pas l'agence par défaut du store), timeline normalisée.
  try {
    const res = await trackShipment({
      tracking: trackingNumber,
      orderId: order?.id,
      agencyCode: String(agencyId || "").toUpperCase(),
    });
    const history = (res?.history || []).map((item) => ({
      status: cleanText(item?.label) || cleanText(item?.status) || "Tracking update",
      date: cleanText(item?.date) || null,
      location: cleanText(item?.location) || "—",
      raw: item,
    }));
    return {
      provider: String(res?.agency_code || agencyId).toLowerCase(),
      providerName: res?.agency_name,
      trackingNumber: res?.tracking_number || trackingNumber,
      trackingStatus: cleanText(res?.status) || history[history.length - 1]?.status || "Tracking available",
      normalizedStatus: res?.normalized_status || null,
      realtime: !!res?.realtime,
      lastUpdate: history[history.length - 1]?.date || null,
      history,
      parcel: res?.carrier_response || null,
      raw: { tracking: res, parcel: res?.carrier_response || null },
      meta: res?.driver
        ? { deliveryAgentName: res.driver.name || null, deliveryAgentPhone: res.driver.phone || null }
        : undefined,
    };
  } catch (error) {
    // 2) repli : anciens adaptateurs côté front
    console.warn("Unified tracking failed, falling back to legacy provider", error?.response?.data || error);
  }

  const provider = Object.values(trackingProviders).find((item) =>
    item.match({ carrier, order, agencyId })
  );

  if (!provider) {
    return {
      provider: null,
      trackingNumber,
      trackingStatus: "Tracking integration not configured",
      lastUpdate: null,
      history: [],
      parcel: null,
      raw: null,
    };
  }

  try {
    return await provider.fetch({
      trackingNumber,
      agencyId,
      carrier,
      order,
    });
  } catch (error) {
    console.error("Tracking fetch failed:", error);

    return {
      provider: null,
      trackingNumber,
      trackingStatus: "Unable to fetch tracking",
      lastUpdate: null,
      history: [],
      parcel: null,
      raw: null,
      error: true,
    };
  }
}
